import React, { useState } from "react";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { Button } from "./catalyst/button";
import { Dialog } from "@headlessui/react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../config/firebaseConfig";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../store";

export interface ReportCardProps {
  title: string;
  description: string;
  status?: "in-progress" | "downloadable" | null | string;
  price?: number;
  onBuy?: (relatedPersonId?: string ) => void;
  isLoading?: boolean;
  disabled?: boolean;
  imageUrl?: string;
  isAvailable?: boolean;
  isRelationship?: boolean;
  predecessorTitles?: string[]; // ✅ Already here
  previousReportOutdated?: boolean;
  userId: string;
  reportId?:string,
  relatedData?: {
    relatedName:string | null,
    relatedRelation: string | null
  }
}

const ReportCard: React.FC<ReportCardProps> = ({
  title,
  description,
  status,
  price,
  onBuy,
  isLoading: externalLoading,
  disabled,
  imageUrl,
  isAvailable = true,
  isRelationship = false,
  predecessorTitles = [],
  previousReportOutdated,
  userId,
  reportId,
  relatedData
}) => {
  const highRes = imageUrl;
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  const [showMissingDialog, setShowMissingDialog] = useState(false);
  const [showRelatedDialog, setShowRelatedDialog] = useState(false);
  const [showAddRelatedDialog, setShowAddRelatedDialog] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<string | null>(null);
  const [relatedPersons, setRelatedPersons] = useState<any[]>([]);
  const [showOutdatedDialog, setShowOutdatedDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user); // ✅ HERE
  const isAdmin = user?.role === "admin";

  // 🔥 Fetch related persons dynamically
  const fetchRelatedPersons = async () => {
    try {
      const userRef = doc(db, "users", userId);
      const snap = await getDoc(userRef);
      if (!snap.exists()) return [];

      const data = snap.data();
      const rawRelated =
        data?.userData?.profile?.relatedPersons ??
        data?.profile?.relatedPersons ??
        {};

      const relatedArray = Object.entries(rawRelated).map(([id, person]: any) => ({
        id,
        ...person
      }));

      return relatedArray;

    } catch (error) {
      console.error("Error fetching related persons:", error);
      return [];
    }
  };

  // 🧩 Unlock flow
  const handleUnlockClick = async (isReRun:boolean) => {
    console.log(isReRun)
    if (disabled || isLoading) return;
    setIsLoading(true);
    if(!isReRun){
      try {
        if (previousReportOutdated) {
          setShowOutdatedDialog(true);
          return;
        }
        if (!isAvailable) {
          setShowMissingDialog(true);
          return;
        }
        if (isRelationship) {
          const persons = await fetchRelatedPersons();
          setRelatedPersons(persons);
          if (persons.length === 0) setShowAddRelatedDialog(true);
          else setShowRelatedDialog(true);
          return;
        }
        onBuy?.();
      } finally {
        setIsLoading(false);
      }
    }else{
      try {
        if (previousReportOutdated) {
          setShowOutdatedDialog(true);
          return;
        }
        if (!isAvailable) {
          setShowMissingDialog(true);
          console.log("1")
          return;

        }
        if (isRelationship) {
          const persons = await fetchRelatedPersons();
          setRelatedPersons(persons);
          console.log("2")
          if (persons.length === 0) setShowAddRelatedDialog(true);
          else setShowRelatedDialog(true);
          return;
        }
        console.log("3")
        await onBuy?.();
      } finally {
        setIsLoading(false);
      }
    }
    
  };

  const handleViewReport = (id?:string) =>{
    // eslint-disable-next-line react-hooks/rules-of-hooks
    
    if(id){
      if(isAdmin){
        navigate(`/admin/user-view/view-report/${id}`)
      }else{
        navigate(`/user/view-report/${id}`)
      }
      
    }
    
  }
  return (
    <>
      {/* 🧱 Card */}
      <div className="relative max-w-[280px] h-[400px] rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-shadow flex flex-col">
        {/* Background */}
        <div
          className={`absolute inset-0 transition-opacity duration-500 ${
            imgLoaded && !imgError ? "opacity-0" : "opacity-100"
          }`}
          style={{
            background: "linear-gradient(180deg, #FFFFFF 0%, #6F148D 100%)",
          }}
        />
        {highRes && (
          <img
            src={highRes}
            alt={title}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
              imgLoaded && !imgError ? "opacity-100" : "opacity-0"
            }`}
          />
        )}

        {/* Status */}
        {(status === "sent" || !status) ? null : 
          status === "outdated" ? (
            <span className="absolute top-3 right-3 bg-gray-600 text-white text-xs font-semibold px-2 py-1 rounded">
              outdated
            </span>
          ) : (
            <span className="absolute top-3 right-3 bg-[rgba(254,236,220,1)] text-[rgba(208,56,1,1)] text-xs font-semibold px-2 py-1 rounded">
              in progress
            </span>
          )}


        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 h-[200px] p-4 flex flex-col bg-black/60">

  {/* CLICK LOGIC */}
  <div
    onClick={
      (status === "sent" || status === "outdated")
        ? () => handleViewReport(reportId)        // only clickable when sent
        : undefined
    }
    className={`
      ${
        (status === "sent" || status === "outdated")
          ? "cursor-pointer hover:opacity-90 transition"
          : "cursor-not-allowed opacity-70"
      }
    `}
  >
    <h4 className="text-white font-bold text-base truncate">
      {title}
    </h4>
    {(isRelationship && status)&& <div className="text-white font-semibold text-[12px] line-clamp-2">
      with {relatedData?.relatedName}, {relatedData?.relatedRelation}
    </div>}

    <p className="mt-1 text-sm text-white line-clamp-3">
      {description}
    </p>
  </div>

  {/* BUTTON WHEN NO STATUS */}
  {!status && (
    <div className="mt-auto flex items-center justify-between">

      <span className="text-base font-semibold text-white">
        ${price ?? 46}
      </span>

      <Button
        type="button"
        color="gradient"
        className="px-3 py-1.5 text-sm font-medium rounded-xl shadow-sm"
        onClick={(e:any) => {
          e.stopPropagation();
          handleUnlockClick(false);
        }}
        disabled={disabled || isLoading || externalLoading}
      >
        {isLoading || externalLoading ? (
          <span className="flex items-center gap-1">
            <svg
              className="animate-spin h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
            Loading...
          </span>
        ) : (
          "Unlock report"
        )}
      </Button>
    </div>
  )}
  {status == "outdated" && (
    <div className="mt-auto flex items-center justify-between">

      <span className="text-base font-semibold text-white">
        {/* ${price ?? 46} */}
      </span>

      <Button
        type="button"
        color="gradient"
        className="px-3 py-1.5 text-sm font-medium rounded-xl shadow-sm"
        onClick={(e:any) => {
          e.stopPropagation();
          handleUnlockClick(true);
        }}
        disabled={disabled || isLoading || externalLoading}
      >
        {isLoading || externalLoading ? (
          <span className="flex items-center gap-1">
            <svg
              className="animate-spin h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
            Loading...
          </span>
        ) : (
          `Re-run`
        )}
      </Button>
    </div>
  )}
</div>

      </div>


      {/* ❌ Missing Reports Dialog */}
      <Dialog open={showMissingDialog} onClose={() => setShowMissingDialog(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="bg-[#0B1120] text-white p-6 rounded-xl shadow-lg max-w-xl w-full">
            <Dialog.Title className="text-lg font-semibold mb-2">
              Previous reports required
            </Dialog.Title>
            <p className="text-sm text-gray-300 mb-3">
              This report uses information from:
            </p>
            <ul className="list-disc pl-5 text-sm text-gray-200 mb-3">
              {predecessorTitles.map((t, i) => (
                <li key={i}>{t}</li>
              ))}
            </ul>
            <p className="text-xs text-gray-400 mb-4">
              One or more of these reports have not been purchased.
            </p>
            <div className="flex justify-end gap-2">
              <Button color="soft" onClick={() => setShowMissingDialog(false)}>
                Close
              </Button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* 👥 Select Related Person Dialog */}
      <Dialog open={showRelatedDialog} onClose={() => setShowRelatedDialog(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="bg-[#1A2235] text-white p-6 rounded-xl shadow-lg max-w-2xl w-full">
            <Dialog.Title className="text-xl font-semibold mb-1">
              Select a related person
            </Dialog.Title>
            <p className="text-normal font-base mb-3 text-gray-300">
              This report uses information about you and those who have a relationship with you.
            </p>
            <p className="text-normal font-semibold mb-3 text-indigo-400">
              You cannot change your selection after purchase.
            </p>

            <div className="space-y-2">
            {relatedPersons.map((person) => (
              <label key={person.id}
                  className={`flex justify-between items-center p-3 rounded-lg cursor-pointer ${
                    selectedPerson === person.id
                      ? "bg-indigo-600"
                      : "bg-gray-800 hover:bg-gray-700"
                  }`}
                >
                  <span>{person.identity?.fullName ?? `Person ${person.id + 1}`}</span>
                  <input
                    type="radio"
                    name="relatedPerson"
                    checked={selectedPerson === person.id}
                    onChange={() => setSelectedPerson(person.id)}
                  />
                </label>
              ))}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <Button color="darktheme" onClick={() => setShowRelatedDialog(false)}>
                Cancel
              </Button>
              <Button
                color="brand"
                disabled={!selectedPerson}
                onClick={() => {
                  if (selectedPerson) {
                    onBuy?.( selectedPerson);
                    setShowRelatedDialog(false);
                  }
                }}
              >
                Continue
              </Button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* ➕ Add Related Person Dialog */}
      <Dialog open={showAddRelatedDialog} onClose={() => setShowAddRelatedDialog(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="bg-[#1A2235] text-white p-6 rounded-xl shadow-lg max-w-xl w-full">
            <Dialog.Title className="text-lg font-semibold mb-2">
              Please add a related person’s profile
            </Dialog.Title>
            <p className="text-sm text-gray-300 mb-12">
              To run a relationship report, we need information about the other person.
            </p>
            <div className="flex justify-end gap-2">
              <Button color="darktheme" onClick={() => setShowAddRelatedDialog(false)}>
                Cancel
              </Button>
              <Button color="brand" onClick={() => navigate(`/user/profile`)}>
                Add a profile
              </Button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>


      {/* 🔥 Outdated Predecessor Dialog */}
<Dialog open={showOutdatedDialog} onClose={() => setShowOutdatedDialog(false)} className="relative z-50">
  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
  <div className="fixed inset-0 flex items-center justify-center p-4">
    <Dialog.Panel className="bg-[#0B1120] text-white p-6 rounded-xl shadow-lg max-w-xl w-full">
      
      <Dialog.Title className="text-lg font-semibold mb-3">
        Re-run outdated reports
      </Dialog.Title>

      <p className="text-sm text-gray-300 mb-2">
        This report uses information from:
      </p>

      <ul className="list-disc pl-5 text-sm text-gray-200 mb-4">
        {predecessorTitles.map((t, i) => (
          <li key={i}>{t}</li>
        ))}
      </ul>

      <p className="text-xs text-gray-400 mb-4">
        One or more of these reports are outdated. Please re-run them before unlocking this report.
      </p>

      <div className="flex justify-end">
        <Button color="soft" onClick={() => setShowOutdatedDialog(false)}>
          Close
        </Button>
      </div>

    </Dialog.Panel>
  </div>
</Dialog>

    </>
  );
};

export default ReportCard;
